import { Preferences } from '@capacitor/preferences';

/** Namespaced keys — one canonical list, referenced by stores. */
export const StorageKeys = {
  AuthToken: 'auth.token',
  AuthUser: 'auth.user',
  AuthSubdomain: 'auth.subdomain',
  AuthIssuedAt: 'auth.issuedAt',
  AuthAcl: 'auth.acl',
  AppTheme: 'app.theme',
  AppLocale: 'app.locale',
  AppAccent: 'app.accent',
  AppDateFormat: 'app.dateFormat',
  AppTimeFormat: 'app.timeFormat',
  AppNumberFormat: 'app.numberFormat',
  AppCurrencyPosition: 'app.currencyPosition',
  AppWeekStart: 'app.weekStart',
  AppPosHideOutOfStock: 'app.posHideOutOfStock',
  ActiveCompanyId: 'context.companyId',
  ActiveBranchId: 'context.branchId',
  ViewAllBranches: 'context.allBranches',
  OnboardingDismissed: 'onboarding.dismissed',
  PrinterDevice: 'printer.device',
  FiscalKey: 'fiscal.key',
  PosNumberReservation: 'pos.numberReservation',
  // Reservations awaiting close (void-unused) that failed to close while offline —
  // retried on the next online release so a rotation never leaks a permanent gap.
  PosPendingCloses: 'pos.pendingCloses',
  CentralAuthToken: 'central.auth.token',
  CentralAuthAdmin: 'central.auth.admin',
} as const;

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys];

async function getRaw(key: StorageKey): Promise<string | null> {
  const { value } = await Preferences.get({ key });
  return value ?? null;
}

async function setRaw(key: StorageKey, value: string): Promise<void> {
  await Preferences.set({ key, value });
}

async function removeRaw(key: StorageKey): Promise<void> {
  await Preferences.remove({ key });
}

export const secure = {
  async getString(key: StorageKey): Promise<string | null> {
    return getRaw(key);
  },

  async setString(key: StorageKey, value: string): Promise<void> {
    await setRaw(key, value);
  },

  async getJson<T>(key: StorageKey): Promise<T | null> {
    const raw = await getRaw(key);
    // Older builds could write the literal string "undefined" via
    // JSON.stringify(undefined); treat those as absent instead of throwing on
    // parse. See fiscal.service.ts for the write-side fix.
    if (raw == null || raw === 'undefined') return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },

  async setJson<T>(key: StorageKey, value: T): Promise<void> {
    await setRaw(key, JSON.stringify(value));
  },

  async remove(key: StorageKey): Promise<void> {
    await removeRaw(key);
  },

  async clearAuth(): Promise<void> {
    await Promise.all([
      removeRaw(StorageKeys.AuthToken),
      removeRaw(StorageKeys.AuthUser),
      removeRaw(StorageKeys.AuthSubdomain),
      removeRaw(StorageKeys.AuthIssuedAt),
      removeRaw(StorageKeys.AuthAcl),
      removeRaw(StorageKeys.ActiveCompanyId),
      removeRaw(StorageKeys.ActiveBranchId),
      removeRaw(StorageKeys.ViewAllBranches),
    ]);
  },

  async clearCentralAuth(): Promise<void> {
    await Promise.all([
      removeRaw(StorageKeys.CentralAuthToken),
      removeRaw(StorageKeys.CentralAuthAdmin),
    ]);
  },
};

// NOTE: biometric-gated secure storage (native keychain) lands in a later
// milestone alongside the real `useBiometric()` implementation. For M1 the
// Capacitor Preferences plugin is the single storage surface — sufficient
// on web (localStorage-backed) and native (platform keystore-backed).
